// src/pages/Waitlist.tsx
'use client';

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Zap, Dumbbell, Brain, MessageCircle, Activity, Trophy } from 'lucide-react';
import { getSupabase } from '@/lib/supabase';
import AICoachMockup from '@/components/marketing/CoachChatMock';

const WAITLIST_MULTIPLIER = 3;

export default function Waitlist() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [signupCount, setSignupCount] = useState<number | null>(null);
  const [shareCode, setShareCode] = useState('');
  const [copied, setCopied] = useState(false);

  const displayCount = signupCount !== null ? signupCount * WAITLIST_MULTIPLIER : null;

  // Capture UTM source from social media links
  const utmSource = searchParams.get('utm_source') || searchParams.get('ref') || null;

  useEffect(() => {
    // Fetch waitlist count for social proof
    (async () => {
      const supabase = getSupabase();
      if (!supabase) return;
      const { count } = await supabase
        .from('waitlist')
        .select('*', { count: 'exact', head: true });
      if (count !== null) setSignupCount(count);
    })();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError('');

    const supabase = getSupabase();
    if (!supabase) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
      return;
    }

    const { error: insertError } = await supabase.from('waitlist').insert({
      email: email.trim().toLowerCase(),
      name: name.trim() || null,
      referral_source: utmSource,
    });

    if (insertError) {
      if (insertError.code === '23505') {
        // Duplicate email — still show success
        setSuccess(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } else {
      setSuccess(true);
      if (signupCount !== null) setSignupCount(signupCount + 1);
      // Generate a simple share code from email for referral tracking
      const code = btoa(email.trim().toLowerCase()).replace(/[^a-zA-Z0-9]/g, '').slice(0, 8);
      setShareCode(code);
    }
    setLoading(false);
  };

  const features = [
    {
      icon: <Brain className="w-6 h-6" />,
      title: 'AI-Powered Coaching',
      desc: 'Personalised training and nutrition plans that adapt to your progress, recovery, and goals.',
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: 'In-App AI Coach',
      desc: 'Get instant coaching, meal ideas, and workout guidance directly in the app — 24/7.',
    },
    {
      icon: <Dumbbell className="w-6 h-6" />,
      title: 'Smart Training Programs',
      desc: 'Programs for running, cycling, CrossFit, HYROX, triathlon and more — built by AI, refined by you.',
    },
    {
      icon: <Activity className="w-6 h-6" />,
      title: 'Live Workout Tracking',
      desc: 'Connect via Bluetooth to rowers, bikes, and heart rate straps for real-time performance data.',
    },
    {
      icon: <Trophy className="w-6 h-6" />,
      title: 'Gamification & Social',
      desc: 'XP, achievements, streaks, leaderboards and challenges to keep you motivated every single day.',
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Device Integrations',
      desc: 'Syncs with Garmin, WHOOP, and Apple Health for a complete picture of your training and recovery.',
    },
  ];

  // ——— SUCCESS STATE — Viral Share Loop ———
  if (success) {
    const shareUrl = `${window.location.origin}/waitlist?ref=${shareCode}`;
    const shareText = `I just joined the Input waitlist — AI coaching for training, nutrition & recovery. Join me 👇\n${shareUrl}`;

    const copyShareLink = () => {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    const shareWhatsApp = () => {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
    };

    const shareNative = async () => {
      if (!navigator.share) return;
      try {
        await navigator.share({
          title: 'Join Input',
          text: 'I just joined the Input waitlist — AI coaching for training, nutrition & recovery.',
          url: shareUrl,
        });
      } catch {}
    };

    return (
      <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
        <div className="fixed inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-[#ffffff] via-[#ffffff] to-[#ffffff]" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#a78bfa]/5 rounded-full blur-[120px]" />
        </div>

        <main className="flex items-center justify-center min-h-screen p-6">
          <div className="text-center max-w-lg w-full">
            {/* Animated checkmark */}
            <div className="relative w-24 h-24 mx-auto mb-8">
              <div className="absolute inset-0 bg-[#a78bfa]/20 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
              <div className="relative w-24 h-24 bg-[#a78bfa] rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-[#0f172a]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-4">You're on the list</h1>
            <p className="text-xl text-[#a78bfa] font-semibold mb-3">Early access secured</p>

            {/* Position card */}
            {displayCount !== null && displayCount > 5 && (
              <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-6 mb-6 inline-block">
                <p className="text-gray-500 text-sm mb-1">Your position</p>
                <p className="text-4xl font-bold text-[#0f172a] mb-1">#{displayCount.toLocaleString()}</p>
                <p className="text-gray-500 text-sm">of <span className="text-[#a78bfa] font-semibold">{displayCount.toLocaleString()}+</span> athletes</p>
              </div>
            )}

            {/* Share to move up */}
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-6 mb-6 text-left">
              <div className="text-center mb-5">
                <h3 className="text-lg font-bold text-[#0f172a] mb-1">Move up the list</h3>
                <p className="text-gray-500 text-sm">Share your link — when friends join, you move closer to the front.</p>
              </div>

              {/* Share link display */}
              <div className="bg-[#f1f5f9] rounded-xl p-3 mb-4">
                <p className="text-xs font-mono text-[#a78bfa] break-all select-all">{shareUrl}</p>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <button
                  onClick={copyShareLink}
                  className="flex-1 py-3 bg-[#a78bfa] hover:bg-[#8b5cf6] text-[#0f172a] font-bold rounded-xl transition-all flex items-center justify-center gap-2 text-sm"
                >
                  {copied ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                      Copy Link
                    </>
                  )}
                </button>

                <button
                  onClick={shareWhatsApp}
                  className="px-5 py-3 bg-[#25D366] hover:bg-[#a78bfa] text-[#0f172a] font-bold rounded-xl transition-all flex items-center justify-center"
                  title="Share via WhatsApp"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </button>

                {typeof navigator !== 'undefined' && navigator.share && (
                  <button
                    onClick={shareNative}
                    className="px-5 py-3 bg-[#f1f5f9] hover:bg-[#f1f5f9] text-[#0f172a] rounded-xl transition-all flex items-center justify-center"
                    title="Share"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                  </button>
                )}
              </div>
            </div>

            <Link
              to="/"
              className="text-gray-500 hover:text-[#0f172a] transition-colors text-sm"
            >
              Back to Home
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // ——— MAIN WAITLIST PAGE ———
  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a] overflow-x-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#ffffff] via-[#ffffff] to-[#ffffff]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#a78bfa]/5 rounded-full blur-[120px]" />
      </div>

      {/* Floating particles */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {[...Array(10)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#a78bfa]/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${18 + Math.random() * 15}s infinite ease-in-out`,
              animationDelay: `${i * 0.5}s`,
            }}
          />
        ))}
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#e2e8f0]">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-500 hover:text-[#a78bfa] transition-colors text-sm">
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-[#a78bfa] text-xl">•</span>
            <span className="font-semibold text-[#0f172a] text-sm sm:text-base">Input</span>
          </div>
          <div className="w-20" /> {/* Spacer for centering */}
        </div>
      </header>

      {/* Hero Section — centred messaging + form */}
      <section className="relative z-10 px-4 sm:px-6 pt-12 sm:pt-20 pb-8 sm:pb-12 max-w-3xl mx-auto text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#a78bfa]/10 border border-[#a78bfa]/25 rounded-full text-sm text-[#a78bfa] font-medium mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-[#a78bfa] animate-pulse" />
          Early Access — Limited Spots
        </div>

        <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold leading-[1.05] mb-6">
          Training. Nutrition.
          <br />
          Sleep. Recovery.
          <br />
          <span className="text-[#a78bfa]">One AI Coach.</span>
        </h1>

        <p className="text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">
          Stop juggling separate apps for workouts, meal plans, sleep tracking, and recovery. Input connects everything into one intelligent platform, and coaches you through it all.
        </p>

        {/* Pillar pills */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
          {[
            { label: 'Training', icon: '🏋️' },
            { label: 'Nutrition', icon: '🥗' },
            { label: 'Sleep', icon: '🌙' },
            { label: 'Recovery', icon: '⚡' },
            { label: 'Coaching', icon: '🧠' },
          ].map((p) => (
            <span
              key={p.label}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-white/[0.04] border border-[#e2e8f0] rounded-full text-sm text-gray-600 font-medium"
            >
              <span className="text-base">{p.icon}</span>
              {p.label}
            </span>
          ))}
        </div>

        {/* Signup Form */}
        <form onSubmit={handleSubmit} className="max-w-md mx-auto mb-6">
          <div className="space-y-3 mb-4">
            <input
              type="text"
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-5 py-4 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] text-[#0f172a] placeholder-gray-500 focus:border-[#a78bfa] focus:outline-none transition-colors text-base"
            />
            <input
              required
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-5 py-4 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] text-[#0f172a] placeholder-gray-500 focus:border-[#a78bfa] focus:outline-none transition-colors text-base"
            />
          </div>
          <button
            type="submit"
            disabled={loading || !email.trim()}
            className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
              email.trim()
                ? 'bg-[#a78bfa] hover:bg-[#8b5cf6] text-[#0f172a] cursor-pointer shadow-lg shadow-[#a78bfa]/25 hover:shadow-[#a78bfa]/40'
                : 'bg-[#f1f5f9] text-gray-500 cursor-not-allowed'
            }`}
          >
            {loading ? 'Joining...' : 'Join the Waitlist'}
          </button>
          {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
        </form>

        <p className="text-gray-600 text-sm">7 Day Free Trial, We'll notify you when it's your turn.</p>

        {/* Social proof */}
        {displayCount !== null && displayCount > 5 && (
          <div className="mt-8 flex items-center justify-center gap-3">
            <div className="flex -space-x-2">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="w-8 h-8 rounded-full border-2 border-[#e2e8f0]"
                  style={{ background: ['#a78bfa', '#3b82f6', '#a855f7', '#f97316'][i] }}
                />
              ))}
            </div>
            <p className="text-gray-500 text-sm">
              <span className="text-[#0f172a] font-semibold">{displayCount.toLocaleString()}+</span> athletes already waiting
            </p>
          </div>
        )}
      </section>

      {/* Integration badges */}
      <section className="relative z-10 px-4 sm:px-6 pb-12 sm:pb-16">
        <div className="flex flex-wrap items-center justify-center gap-3 max-w-lg mx-auto">
          <span className="text-gray-600 text-sm mr-1">Syncs with</span>
          {['Garmin', 'WHOOP', 'Apple Health', 'Concept2'].map((n) => (
            <span
              key={n}
              className="px-3 py-1.5 bg-white/[0.03] border border-[#e2e8f0] rounded-lg text-xs text-gray-500 font-medium"
            >
              {n}
            </span>
          ))}
        </div>
      </section>

      {/* App Preview Section — inline phone mockups */}
      <section className="relative z-10 px-4 sm:px-6 py-16 sm:py-24 border-t border-[#e2e8f0]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-[#a78bfa]/10 border border-[#a78bfa]/25 rounded-full text-sm text-[#a78bfa] font-medium mb-6">
              Inside the App
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Everything connects
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Your sleep data informs your training. Your training shapes your nutrition. Your recovery determines tomorrow's plan. One AI that sees the full picture.
            </p>
          </div>

          {/* Row 1: 3 phone mockups */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 mb-10 md:mb-14">
            {/* Phone 1: Training Readiness */}
            <div className="flex flex-col items-center">
              <div className="wl-phone-mockup mb-6">
                <div className="wl-phone-notch" />
                <div className="wl-phone-screen">
                  <ReadinessScreen />
                </div>
                <div className="wl-phone-homebar">
                  <div className="wl-phone-homebar-pill" />
                </div>
              </div>
              <h3 className="text-base font-semibold mb-1">Sleep & Readiness</h3>
              <p className="text-sm text-gray-500 text-center max-w-[260px]">Sleep, HRV, and recovery data fused into a daily readiness score that adapts your training</p>
            </div>

            {/* Phone 2: Live Workout Tracking */}
            <div className="flex flex-col items-center">
              <div className="wl-phone-mockup mb-6">
                <div className="wl-phone-notch" />
                <div className="wl-phone-screen">
                  <LiveWorkoutScreen />
                </div>
                <div className="wl-phone-homebar">
                  <div className="wl-phone-homebar-pill" />
                </div>
              </div>
              <h3 className="text-base font-semibold mb-1">Live Training</h3>
              <p className="text-sm text-gray-500 text-center max-w-[260px]">Real-time data from rowers, bikes, and HR straps — with AI pacing guidance</p>
            </div>

            {/* Phone 3: Post-Workout Nutrition */}
            <div className="flex flex-col items-center">
              <div className="wl-phone-mockup mb-6">
                <div className="wl-phone-notch" />
                <div className="wl-phone-screen">
                  <PostWorkoutScreen />
                </div>
                <div className="wl-phone-homebar">
                  <div className="wl-phone-homebar-pill" />
                </div>
              </div>
              <h3 className="text-base font-semibold mb-1">Nutrition & Recovery</h3>
              <p className="text-sm text-gray-500 text-center max-w-[260px]">AI-generated refuelling plans matched to your workout, goals, and recovery needs</p>
            </div>
          </div>

          {/* Row 2: 2 phone mockups centred */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-6 max-w-[580px] mx-auto">
            {/* Phone 4: Barcode Scanner */}
            <div className="flex flex-col items-center">
              <div className="wl-phone-mockup mb-6">
                <div className="wl-phone-notch" />
                <div className="wl-phone-screen">
                  <BarcodeScannerScreen />
                </div>
                <div className="wl-phone-homebar">
                  <div className="wl-phone-homebar-pill" />
                </div>
              </div>
              <h3 className="text-base font-semibold mb-1">Barcode Scanner</h3>
              <p className="text-sm text-gray-500 text-center max-w-[260px]">Scan any food item to instantly log nutrition and see how it fits your daily targets</p>
            </div>

            {/* Phone 5: Daily Habits */}
            <div className="flex flex-col items-center">
              <div className="wl-phone-mockup mb-6">
                <div className="wl-phone-notch" />
                <div className="wl-phone-screen">
                  <DailyHabitsScreen />
                </div>
                <div className="wl-phone-homebar">
                  <div className="wl-phone-homebar-pill" />
                </div>
              </div>
              <h3 className="text-base font-semibold mb-1">Daily Habits</h3>
              <p className="text-sm text-gray-500 text-center max-w-[260px]">Track hydration, supplements, and daily habits — with streaks and XP to keep you consistent</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Coach Section */}
      <section className="relative z-10 px-4 sm:px-6 py-16 sm:py-24 border-t border-[#e2e8f0]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* LEFT: Copy */}
            <div className="text-center lg:text-left order-2 lg:order-1">
              <span className="inline-block px-4 py-1.5 bg-[#a78bfa]/10 border border-[#a78bfa]/25 rounded-full text-sm text-[#a78bfa] font-medium mb-6">
                AI Coach
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Your coach makes sense of everything
                <br />
                <span className="text-[#a78bfa]">right in the app</span>
              </h2>
              <p className="text-gray-500 max-w-lg mx-auto lg:mx-0 mb-8 leading-relaxed">
                Ask your AI coach anything — adjust today's session based on how you slept, get a meal plan that matches your training load, or check your readiness score. It's like texting a coach who knows your entire training history.
              </p>
              <div className="flex flex-col gap-3 max-w-sm mx-auto lg:mx-0">
                {[
                  { q: '"I slept terribly — should I still do intervals?"', a: 'Coach adapts your session based on sleep + HRV data' },
                  { q: '"What should I eat after this workout?"', a: 'Personalised refuelling plan matched to your session' },
                  { q: '"How ready am I for tomorrow\'s race?"', a: 'Readiness score with training load + recovery insights' },
                ].map((item, i) => (
                  <div key={i} className="p-3 bg-[#f8fafc] border border-[#e2e8f0] rounded-xl text-left">
                    <p className="text-sm text-[#0f172a] font-medium mb-1">{item.q}</p>
                    <p className="text-xs text-gray-500">{item.a}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT: AI Coach Mockup */}
            <div className="flex justify-center lg:justify-end order-1 lg:order-2">
              <div className="wl-phone-float">
                <AICoachMockup />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="relative z-10 px-4 sm:px-6 py-12 sm:py-20 border-t border-[#e2e8f0]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">
            Everything you need to perform
          </h2>
          <p className="text-gray-500 text-center mb-12 max-w-lg mx-auto">
            Training, nutrition, sleep, recovery, and coaching — finally connected in one AI-powered platform.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <div
                key={i}
                className="group p-6 rounded-2xl border border-[#e2e8f0] bg-[#f8fafc]/80 hover:border-[#a78bfa]/30 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-11 h-11 rounded-xl bg-[#a78bfa]/10 border border-[#a78bfa]/20 flex items-center justify-center text-[#a78bfa] mb-4 group-hover:bg-[#a78bfa]/20 transition-colors">
                  {f.icon}
                </div>
                <h3 className="text-base font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative z-10 px-4 sm:px-6 py-16 sm:py-24 border-t border-[#e2e8f0]">
        <div className="max-w-lg mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Don't miss out
          </h2>
          <p className="text-gray-500 mb-8">
            Early adopters get first access and exclusive perks. Join the waitlist now.
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="px-10 py-4 bg-[#a78bfa] text-[#0f172a] rounded-xl font-bold text-lg hover:bg-[#8b5cf6] transition-colors shadow-lg shadow-[#a78bfa]/25"
          >
            Join the Waitlist
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-8 text-center border-t border-[#e2e8f0]">
        <div className="flex items-center justify-center gap-2 mb-4">
          <span className="text-[#a78bfa]">•</span>
          <span className="text-[#0f172a] font-semibold text-sm">Input</span>
        </div>
        <div className="flex gap-6 justify-center mb-4">
          <Link to="/terms" className="text-gray-500 hover:text-[#0f172a] transition text-sm">Terms</Link>
          <Link to="/privacy" className="text-gray-500 hover:text-[#0f172a] transition text-sm">Privacy</Link>
          <Link to="/contact" className="text-gray-500 hover:text-[#0f172a] transition text-sm">Contact</Link>
        </div>
        <p className="text-gray-600 text-xs">
          © {new Date().getFullYear()} Input. All rights reserved.
        </p>
      </footer>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-30px); }
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-14px); }
        }
        .wl-phone-float {
          animation: float-slow 8s infinite ease-in-out;
        }
        .wl-phone-mockup {
          width: 260px;
          max-width: 100%;
          background: #ffffff;
          border-radius: 32px;
          border: 3px solid #e2e8f0;
          box-shadow: 0 40px 100px rgba(15,23,42,0.12), 0 0 60px rgba(167,139,250,0.06);
          overflow: hidden;
        }
        .wl-phone-notch {
          height: 24px;
          background: #ffffff;
          display: flex;
          justify-content: center;
          align-items: flex-end;
          padding-bottom: 4px;
        }
        .wl-phone-notch::after {
          content: '';
          width: 60px;
          height: 5px;
          background: #f1f5f9;
          border-radius: 100px;
        }
        .wl-phone-screen {
          background: #ffffff;
          padding: 12px 10px 16px;
          min-height: 420px;
        }
        .wl-phone-homebar {
          height: 16px;
          background: #ffffff;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .wl-phone-homebar-pill {
          width: 80px;
          height: 4px;
          background: #f1f5f9;
          border-radius: 100px;
        }
      `}</style>
    </div>
  );
}


/* ─── Inline App Screen: Post-Workout Recovery ─── */

function PostWorkoutScreen() {
  const card: React.CSSProperties = { background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0", padding: 10 };
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <div style={{ width: 30, height: 30, borderRadius: "50%", background: "rgba(167,139,250,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
          🏋️
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Post-Workout Recovery</div>
          <div style={{ fontSize: 9, color: "#545f6e" }}>Running · Interval Session</div>
        </div>
      </div>

      <div style={{ ...card, marginBottom: 8 }}>
        <div style={{ fontSize: 8, color: "#545f6e", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Workout Summary</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 2 }}>
          {[
            { label: "Duration", value: "45 min", color: "#0f172a" },
            { label: "Calories", value: "380", color: "#ef4444" },
            { label: "Intensity", value: "High", color: "#ef4444" },
            { label: "Avg HR", value: "162", color: "#ef4444" },
          ].map((s) => (
            <div key={s.label} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 7, color: "#545f6e", textTransform: "uppercase" }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...card, marginBottom: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#0f172a" }}>Refuelling Plan</div>
          <span style={{ fontSize: 7, fontWeight: 600, color: "#a78bfa", background: "rgba(167,139,250,0.15)", padding: "2px 6px", borderRadius: 100 }}>
            Muscle Gain
          </span>
        </div>
        <div style={{ fontSize: 9, color: "#545f6e", lineHeight: 1.5, marginBottom: 8 }}>
          High intensity session — replenish 75% of 380 kcal burned to support recovery and growth.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 2, padding: "8px 0", background: "rgba(255,255,255,0.6)", borderRadius: 8, marginBottom: 6 }}>
          {[
            { label: "Calories", value: "285", unit: "kcal", color: "#a78bfa" },
            { label: "Protein", value: "42", unit: "g", color: "#3b82f6" },
            { label: "Carbs", value: "38", unit: "g", color: "#eab308" },
            { label: "Fat", value: "12", unit: "g", color: "#a855f7" },
          ].map((m) => (
            <div key={m.label} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: m.color }}>
                {m.value}
                <span style={{ fontSize: 7, fontWeight: 400, color: "#545f6e" }}>{m.unit}</span>
              </div>
              <div style={{ fontSize: 7, color: "#545f6e", textTransform: "uppercase" }}>{m.label}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 8px", background: "rgba(59,130,246,0.08)", borderRadius: 6 }}>
          <span style={{ fontSize: 10 }}>💧</span>
          <span style={{ fontSize: 9, color: "#3b82f6", fontWeight: 500 }}>Aim for 750ml water post-workout</span>
        </div>
      </div>

      <div style={{ background: "rgba(167,139,250,0.06)", borderRadius: 12, border: "1px solid rgba(167,139,250,0.25)", padding: 10, position: "relative" }}>
        <span style={{ position: "absolute", top: -7, left: 10, fontSize: 7, fontWeight: 700, color: "#0f172a", background: "#a78bfa", padding: "1px 7px", borderRadius: 100 }}>
          BEST MATCH
        </span>
        <div style={{ marginTop: 2 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 2 }}>Chicken & Rice Bowl</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginBottom: 3 }}>
            <span style={{ fontSize: 7, color: "#545f6e", background: "rgba(15,23,42,0.06)", padding: "1px 5px", borderRadius: 4 }}>High Protein</span>
            <span style={{ fontSize: 7, color: "#545f6e", background: "rgba(15,23,42,0.06)", padding: "1px 5px", borderRadius: 4 }}>Quick Prep</span>
          </div>
          <div style={{ fontSize: 9, color: "#545f6e", marginBottom: 4 }}>Grilled chicken breast with brown rice, avocado & mixed greens</div>
          <div style={{ display: "flex", gap: 6, fontSize: 9, fontWeight: 700 }}>
            <span style={{ color: "#a78bfa" }}>290 kcal</span>
            <span style={{ color: "#3b82f6" }}>42g P</span>
            <span style={{ color: "#eab308" }}>35g C</span>
            <span style={{ color: "#a855f7" }}>8g F</span>
          </div>
        </div>
      </div>
    </>
  );
}


/* ─── Inline App Screen: Live Workout ─── */

function LiveWorkoutScreen() {
  const card: React.CSSProperties = { background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0", padding: 10 };
  const pacePoints = [98, 95, 96, 93, 97, 94, 92, 95, 93, 96, 94, 91, 93, 95, 92, 90, 93, 94, 91, 89];
  const maxP = Math.max(...pacePoints);
  const minP = Math.min(...pacePoints);
  const range = maxP - minP || 1;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Concept2 Rower</div>
          <div style={{ fontSize: 9, color: "#545f6e" }}>5 × 500m Intervals</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#a78bfa" }} />
          <span style={{ fontSize: 8, color: "#a78bfa", fontWeight: 600 }}>Connected</span>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
        <span style={{ padding: "3px 12px", borderRadius: 100, border: "1px solid rgba(167,139,250,0.3)", background: "rgba(167,139,250,0.1)", fontSize: 9, fontWeight: 600, color: "#a78bfa", display: "flex", alignItems: "center", gap: 4 }}>
          <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#a78bfa" }} />
          Rowing
        </span>
      </div>

      <div style={{ textAlign: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 42, fontWeight: 700, color: "#0f172a", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>1:48.2</div>
        <div style={{ fontSize: 9, color: "#545f6e", marginTop: 3 }}>Current /500m</div>
      </div>

      <div style={{ ...card, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0, marginBottom: 8, padding: 0, overflow: "hidden" }}>
        <div style={{ textAlign: "center", padding: 8, borderRight: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>1:49.5</div>
          <div style={{ fontSize: 8, color: "#545f6e" }}>Avg /500m</div>
        </div>
        <div style={{ textAlign: "center", padding: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>1:47.8</div>
          <div style={{ fontSize: 8, color: "#545f6e" }}>Split /500m</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 5, marginBottom: 8 }}>
        {[
          { label: "Time", value: "12:34", color: "#0f172a" },
          { label: "Distance", value: "1,482m", color: "#0f172a" },
          { label: "Stroke Rate", value: "28", unit: "spm", color: "#0f172a" },
          { label: "Power", value: "245", unit: "W", color: "#3b82f6" },
          { label: "Heart Rate", value: "156", unit: "bpm", color: "#ef4444" },
          { label: "Calories", value: "187", unit: "kcal", color: "#f97316" },
        ].map((m) => (
          <div key={m.label} style={{ ...card, textAlign: "center", padding: "6px 4px" }}>
            <div style={{ fontSize: 7, color: "#545f6e", marginBottom: 2 }}>{m.label}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: m.color, fontVariantNumeric: "tabular-nums" }}>
              {m.value}
              {m.unit && <span style={{ fontSize: 7, fontWeight: 400, color: "#545f6e" }}>{m.unit}</span>}
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...card, marginBottom: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontSize: 8, color: "#545f6e", fontWeight: 600 }}>Pace /500m</span>
          <span style={{ fontSize: 8, color: "#a78bfa", fontWeight: 600 }}>Target: 1:50</span>
        </div>
        <div style={{ position: "relative", height: 40 }}>
          <div style={{ position: "absolute", top: "20%", bottom: "30%", left: 0, right: 0, background: "rgba(167,139,250,0.08)", borderRadius: 4 }} />
          <svg width="100%" height="40" viewBox={`0 0 ${pacePoints.length - 1} ${range}`} preserveAspectRatio="none" style={{ display: "block" }}>
            <polyline
              fill="none"
              stroke="#a78bfa"
              strokeWidth="0.8"
              strokeLinejoin="round"
              points={pacePoints.map((p, i) => `${i},${maxP - p}`).join(" ")}
            />
          </svg>
        </div>
      </div>

      <div style={{ ...card, padding: "8px 10px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span style={{ fontSize: 9, fontWeight: 600, color: "#0f172a" }}>Interval 3 of 5</span>
          <span style={{ fontSize: 8, color: "#a78bfa", fontWeight: 600 }}>482m / 500m</span>
        </div>
        <div style={{ height: 4, background: "#f1f5f9", borderRadius: 100, overflow: "hidden" }}>
          <div style={{ width: "96%", height: "100%", background: "linear-gradient(90deg, #3b82f6, #22c55e)", borderRadius: 100 }} />
        </div>
      </div>
    </>
  );
}


/* ─── Inline App Screen: Training Readiness ─── */

function ReadinessScreen() {
  const card: React.CSSProperties = { background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0", padding: 10 };
  const score = 72;
  const scoreColor = "#eab308";
  const radius = 50;
  const strokeWidth = 8;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;

  const factors = [
    { label: "Sleep Duration", value: 78, icon: "🌙", source: "Garmin" },
    { label: "Sleep Quality", value: 65, icon: "🌙", source: "WHOOP" },
    { label: "HRV", value: 58, icon: "💓", source: "Garmin" },
    { label: "Recovery", value: 82, icon: "⚡", source: "WHOOP" },
    { label: "Training Load", value: 70, icon: "🏋️" },
    { label: "Nutrition", value: 88, icon: "🍎" },
  ];

  const getBarColor = (v: number) => {
    if (v >= 80) return "#a78bfa";
    if (v >= 60) return "#eab308";
    if (v >= 40) return "#f97316";
    return "#ef4444";
  };

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 2 }}>Training Readiness</div>
        <div style={{ fontSize: 9, color: "#545f6e" }}>Your body's preparedness to perform</div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 12 }}>
        <div style={{ position: "relative", width: 114, height: 114 }}>
          <svg width={114} height={114} style={{ transform: "rotate(-90deg)" }}>
            <circle cx={57} cy={57} r={radius} fill="none" stroke="#475569" strokeWidth={strokeWidth} />
            <circle
              cx={57} cy={57} r={radius} fill="none" stroke={scoreColor} strokeWidth={strokeWidth}
              strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
            />
          </svg>
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#0f172a" }}>{score}</div>
            <div style={{ fontSize: 8, color: "#545f6e" }}>/ 100</div>
          </div>
        </div>
        <span style={{ marginTop: 8, padding: "3px 12px", borderRadius: 100, background: `${scoreColor}20`, fontSize: 9, fontWeight: 600, color: scoreColor }}>
          Moderate Readiness
        </span>
      </div>

      <div style={{ ...card, marginBottom: 8 }}>
        <div style={{ fontSize: 8, fontWeight: 700, color: "#545f6e", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
          Score Breakdown
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {factors.map((f) => (
            <div key={f.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 20, height: 20, borderRadius: 5, background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, flexShrink: 0 }}>
                {f.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                  <span style={{ fontSize: 9, color: "#545f6e" }}>{f.label}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                    {f.source && (
                      <span style={{ fontSize: 6, color: "#545f6e", background: "rgba(15,23,42,0.05)", padding: "1px 4px", borderRadius: 3 }}>
                        {f.source}
                      </span>
                    )}
                    <span style={{ fontSize: 10, fontWeight: 700, color: getBarColor(f.value) }}>{f.value}</span>
                  </div>
                </div>
                <div style={{ height: 3, background: "#f1f5f9", borderRadius: 100, overflow: "hidden" }}>
                  <div style={{ width: `${f.value}%`, height: "100%", background: getBarColor(f.value), borderRadius: 100 }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...card }}>
        <div style={{ fontSize: 8, fontWeight: 700, color: "#545f6e", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
          AI Briefing
        </div>
        <div style={{ fontSize: 9, color: "#545f6e", lineHeight: 1.5 }}>
          <strong style={{ color: "#0f172a" }}>Sleep & Recovery:</strong> 6.5hrs sleep with moderate quality. HRV trending down.
        </div>
        <div style={{ fontSize: 9, color: "#545f6e", lineHeight: 1.5, marginTop: 4 }}>
          <strong style={{ color: "#0f172a" }}>Coach's Note:</strong> Swap intervals for steady-state zone 2. Focus on recovery today.
        </div>
      </div>
    </>
  );
}


/* ─── Inline App Screen: Barcode Scanner ─── */

function BarcodeScannerScreen() {
  const card: React.CSSProperties = { background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0", padding: 10 };
  const dailyTargets = { calories: 2400, protein: 180, carbs: 280, fat: 80 };
  const eaten = { calories: 1640, protein: 118, carbs: 195, fat: 52 };
  const scannedItem = { name: "Fage Total 0% Greek Yoghurt", brand: "Fage", serving: "200g", calories: 130, protein: 22, carbs: 8, fat: 0 };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <div style={{ width: 30, height: 30, borderRadius: "50%", background: "rgba(59,130,246,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
          📷
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Barcode Scanner</div>
          <div style={{ fontSize: 9, color: "#545f6e" }}>Scan to log instantly</div>
        </div>
      </div>

      {/* Scanner viewfinder area */}
      <div style={{ ...card, marginBottom: 8, padding: 0, overflow: "hidden", position: "relative" }}>
        <div style={{ background: "#080808", height: 100, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
          {/* Scan frame */}
          <div style={{ width: 140, height: 60, position: "relative" }}>
            <div style={{ position: "absolute", top: 0, left: 0, width: 18, height: 18, borderTop: "2px solid #3b82f6", borderLeft: "2px solid #3b82f6", borderRadius: "3px 0 0 0" }} />
            <div style={{ position: "absolute", top: 0, right: 0, width: 18, height: 18, borderTop: "2px solid #3b82f6", borderRight: "2px solid #3b82f6", borderRadius: "0 3px 0 0" }} />
            <div style={{ position: "absolute", bottom: 0, left: 0, width: 18, height: 18, borderBottom: "2px solid #3b82f6", borderLeft: "2px solid #3b82f6", borderRadius: "0 0 0 3px" }} />
            <div style={{ position: "absolute", bottom: 0, right: 0, width: 18, height: 18, borderBottom: "2px solid #3b82f6", borderRight: "2px solid #3b82f6", borderRadius: "0 0 3px 0" }} />
            {/* Barcode lines */}
            <div style={{ position: "absolute", top: 14, left: 20, right: 20, display: "flex", gap: 2, alignItems: "end", height: 32 }}>
              {[18, 28, 12, 22, 8, 26, 14, 30, 10, 24, 16, 28, 20, 12, 26, 18, 22, 14, 28, 10, 24, 16, 30].map((h, i) => (
                <div key={i} style={{ flex: 1, height: h, background: i % 3 === 0 ? "#e2e8f0" : "#e2e8f0", borderRadius: 1 }} />
              ))}
            </div>
            {/* Scan line */}
            <div style={{ position: "absolute", top: "50%", left: 4, right: 4, height: 1, background: "#3b82f6", boxShadow: "0 0 8px rgba(59,130,246,0.6)" }} />
          </div>
        </div>
        <div style={{ padding: "6px 10px", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#a78bfa" }} />
          <span style={{ fontSize: 8, color: "#a78bfa", fontWeight: 600 }}>Item recognised</span>
        </div>
      </div>

      {/* Scanned item result */}
      <div style={{ ...card, marginBottom: 8, background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#0f172a" }}>{scannedItem.name}</div>
            <div style={{ fontSize: 8, color: "#545f6e" }}>{scannedItem.brand} · {scannedItem.serving}</div>
          </div>
          <span style={{ fontSize: 7, fontWeight: 600, color: "#3b82f6", background: "rgba(59,130,246,0.15)", padding: "2px 6px", borderRadius: 100, flexShrink: 0 }}>
            Scanned
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 2 }}>
          {[
            { label: "Calories", value: scannedItem.calories, unit: "kcal", color: "#a78bfa" },
            { label: "Protein", value: scannedItem.protein, unit: "g", color: "#3b82f6" },
            { label: "Carbs", value: scannedItem.carbs, unit: "g", color: "#eab308" },
            { label: "Fat", value: scannedItem.fat, unit: "g", color: "#a855f7" },
          ].map((m) => (
            <div key={m.label} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: m.color }}>
                {m.value}<span style={{ fontSize: 7, fontWeight: 400, color: "#545f6e" }}>{m.unit}</span>
              </div>
              <div style={{ fontSize: 7, color: "#545f6e", textTransform: "uppercase" }}>{m.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Daily totals with progress bars */}
      <div style={{ ...card, marginBottom: 8 }}>
        <div style={{ fontSize: 8, color: "#545f6e", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>After Adding This Item</div>
        {[
          { label: "Calories", current: eaten.calories + scannedItem.calories, target: dailyTargets.calories, unit: "kcal", color: "#a78bfa" },
          { label: "Protein", current: eaten.protein + scannedItem.protein, target: dailyTargets.protein, unit: "g", color: "#3b82f6" },
          { label: "Carbs", current: eaten.carbs + scannedItem.carbs, target: dailyTargets.carbs, unit: "g", color: "#eab308" },
          { label: "Fat", current: eaten.fat + scannedItem.fat, target: dailyTargets.fat, unit: "g", color: "#a855f7" },
        ].map((m) => (
          <div key={m.label} style={{ marginBottom: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
              <span style={{ fontSize: 9, color: "#545f6e" }}>{m.label}</span>
              <span style={{ fontSize: 9, fontWeight: 600, color: "#545f6e" }}>
                {m.current} <span style={{ color: "#545f6e" }}>/ {m.target}{m.unit}</span>
              </span>
            </div>
            <div style={{ height: 4, background: "#f1f5f9", borderRadius: 100, overflow: "hidden" }}>
              <div style={{ width: `${Math.min((m.current / m.target) * 100, 100)}%`, height: "100%", background: m.color, borderRadius: 100 }} />
            </div>
          </div>
        ))}
      </div>

      {/* AI insight */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 10px", background: "rgba(167,139,250,0.06)", borderRadius: 10, border: "1px solid rgba(167,139,250,0.15)" }}>
        <span style={{ fontSize: 12, flexShrink: 0 }}>🧠</span>
        <span style={{ fontSize: 9, color: "#545f6e", lineHeight: 1.4 }}>
          <strong style={{ color: "#a78bfa" }}>Great pick</strong> — high protein, zero fat. You need 40g more protein today, this gets you halfway there.
        </span>
      </div>
    </>
  );
}


/* ─── Inline App Screen: Daily Habits ─── */

function DailyHabitsScreen() {
  const card: React.CSSProperties = { background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0", padding: 10 };

  const habits = [
    { name: "Creatine (5g)", icon: "💊", done: true, streak: 42, xp: 10 },
    { name: "Omega-3", icon: "🐟", done: true, streak: 38, xp: 10 },
    { name: "Vitamin D3", icon: "☀️", done: false, streak: 15, xp: 10 },
    { name: "10 min stretch", icon: "🧘", done: true, streak: 12, xp: 15 },
    { name: "8hrs sleep target", icon: "🌙", done: false, streak: 5, xp: 20 },
    { name: "Cold exposure", icon: "🧊", done: false, streak: 8, xp: 25 },
  ];

  const waterGlasses = 6;
  const waterTarget = 10;
  const completedCount = habits.filter((h) => h.done).length;
  const totalXP = habits.filter((h) => h.done).reduce((sum, h) => sum + h.xp, 0);

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Daily Habits</div>
          <div style={{ fontSize: 9, color: "#545f6e" }}>Tuesday, 27 Feb</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 8px", background: "rgba(234,179,8,0.1)", borderRadius: 100 }}>
          <span style={{ fontSize: 10 }}>⚡</span>
          <span style={{ fontSize: 9, fontWeight: 700, color: "#eab308" }}>+{totalXP} XP</span>
        </div>
      </div>

      {/* Progress ring */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <div style={{ position: "relative", width: 52, height: 52, flexShrink: 0 }}>
          <svg width={52} height={52} style={{ transform: "rotate(-90deg)" }}>
            <circle cx={26} cy={26} r={22} fill="none" stroke="#475569" strokeWidth={4} />
            <circle
              cx={26} cy={26} r={22} fill="none" stroke="#a78bfa" strokeWidth={4}
              strokeLinecap="round"
              strokeDasharray={22 * 2 * Math.PI}
              strokeDashoffset={22 * 2 * Math.PI - (completedCount / habits.length) * 22 * 2 * Math.PI}
            />
          </svg>
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", fontSize: 12, fontWeight: 700, color: "#0f172a" }}>
            {completedCount}/{habits.length}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#0f172a", marginBottom: 2 }}>{completedCount} of {habits.length} completed</div>
          <div style={{ fontSize: 9, color: "#545f6e" }}>Complete all habits for a 2x XP bonus</div>
        </div>
      </div>

      {/* Hydration tracker */}
      <div style={{ ...card, marginBottom: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 10 }}>💧</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: "#0f172a" }}>Hydration</span>
          </div>
          <span style={{ fontSize: 9, color: "#3b82f6", fontWeight: 600 }}>{waterGlasses * 250}ml / {waterTarget * 250}ml</span>
        </div>
        <div style={{ display: "flex", gap: 3, marginBottom: 4 }}>
          {[...Array(waterTarget)].map((_, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: 20,
                borderRadius: 4,
                background: i < waterGlasses ? "rgba(59,130,246,0.3)" : "#f1f5f9",
                border: i < waterGlasses ? "1px solid rgba(59,130,246,0.4)" : "1px solid #e2e8f0",
                display: "flex",
                alignItems: "flex-end",
                overflow: "hidden",
              }}
            >
              {i < waterGlasses && (
                <div style={{ width: "100%", height: "100%", background: "linear-gradient(to top, #3b82f6, rgba(59,130,246,0.4))", borderRadius: 3 }} />
              )}
            </div>
          ))}
        </div>
        <div style={{ fontSize: 8, color: "#545f6e", textAlign: "center" }}>Tap to log a glass (250ml)</div>
      </div>

      {/* Habit checklist */}
      <div style={{ ...card }}>
        <div style={{ fontSize: 8, color: "#545f6e", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
          Supplements & Habits
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {habits.map((h) => (
            <div key={h.name} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{
                width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                background: h.done ? "#a78bfa" : "transparent",
                border: h.done ? "none" : "1.5px solid #e2e8f0",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {h.done && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span style={{ fontSize: 11, flexShrink: 0 }}>{h.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 10, color: h.done ? "#545f6e" : "#0f172a", fontWeight: h.done ? 400 : 500, textDecoration: h.done ? "line-through" : "none" }}>
                  {h.name}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                <span style={{ fontSize: 7, color: "#f97316", fontWeight: 600 }}>🔥 {h.streak}d</span>
                <span style={{ fontSize: 7, color: "#545f6e" }}>+{h.xp}xp</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
